import { useQueryClient } from '@tanstack/react-query';
import { useField } from 'formik';
import Skeleton from 'react-loading-skeleton';

import type { AgentMessage } from '../../../types/AgentMessage';
import { Button } from '../../cdl/Button/Button';
import { Callout } from '../../cdl/Callout/Callout';
import { AsyncComboBox, ComboBox } from '../../cdl/ComboBox/ComboBox';
import { useDrawer } from '../../cdl/Drawer/useDrawer.hook';
import { Optional } from '../../cdl/Input/components/Optional';
import type { GetAgentListParams } from '../../common/api/clients/agent.api';
import { queries } from '../../common/api/queryKeys/queries';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { IconInfoCircle } from '../../common/icons/cdl/InfoCircle';
import { IconPencil } from '../../common/icons/cdl/Pencil';
import { IconPlus } from '../../common/icons/cdl/Plus';
import { LabelText } from '../../common/LabelText/LabelText';
import { Box } from '../../common/ui/Box';
import { CreateAgentForm } from '../CreateAgentForm/CreateAgentForm';
import { UpdateAgentForm } from '../UpdateAgentForm';

import type { AgentSelectOption } from './AgentSelectOption';
import { AgentOption } from './components/AgentOption';
import { AgentValue } from './components/AgentValue';
import { useAgentList } from './hooks/useAgentList';

const createAgentOption = (agent: AgentMessage): AgentSelectOption => {
    return {
        value: agent.id,
        label: agent.name,
        agent: agent,
    };
};

interface FormikAgentSelectFlowProps {
    name: string;
    customerId?: string;
    portId?: string;
    label: string;
    markAsOptional?: boolean;
}

const AgentSelectLabel = ({ label, markAsOptional }: Pick<FormikAgentSelectFlowProps, 'label' | 'markAsOptional'>) => (
    <LabelText>
        {label}
        {markAsOptional ? <Optional /> : null}
    </LabelText>
);

export const FormikAgentSelectFlow = ({
    name,
    customerId,
    portId,
    label,
    markAsOptional,
    ...props
}: FormikAgentSelectFlowProps) => {
    const [field, , helpers] = useField(name);
    const role = useRole();
    const queryClient = useQueryClient();

    const { push, pop } = useDrawer();
    const customerIds = customerId ? [customerId] : [];
    const portIds = portId ? [portId] : [];
    const agentListQuery = useAgentList({ customerIds, portIds });

    const selectedAgentId = field.value;
    const selectedAgent = agentListQuery.data?.agents.find((agent: AgentMessage) => agent.id === selectedAgentId);

    const getAgentListParams = (searchQuery?: string): GetAgentListParams => ({
        customerIds,
        portIds,
        searchQuery: searchQuery?.trim() || undefined,
    });

    const loadAgentOptions = async (search: string): Promise<AgentSelectOption[]> => {
        const response = await queryClient.fetchQuery(queries.agents.list(getAgentListParams(search)));

        return [
            ...response.agents.map(createAgentOption),
            {
                value: 'createAgent',
                label: translate('selectAgent.createAgent'),
                agentName: search.trim(),
            },
        ];
    };

    const loadSelectedAgentOption = async (agentId: string): Promise<AgentSelectOption | null> => {
        const params = { ...getAgentListParams(), ids: [agentId] };
        const response = await queryClient.fetchQuery(queries.agents.list(params));
        const agent = response.agents.find((agentMessage) => agentMessage.id === agentId);

        return agent ? createAgentOption(agent) : null;
    };

    const onAgentFormSubmitSuccess = (agent?: AgentMessage) => {
        if (agent) {
            helpers.setValue(agent.id);
        }
        queryClient.invalidateQueries({ queryKey: queries.agents._def });
    };

    const openCreateAgentModal = (option?: AgentSelectOption) => {
        const drawerId = push({
            header: { title: translate('agents.createAgent') },
            content: (
                <CreateAgentForm
                    data={{
                        name: option?.agentName || '',
                        customerId: customerId,
                        portIds: portId ? [portId] : [],
                    }}
                    onSuccess={(agent) => {
                        onAgentFormSubmitSuccess(agent);
                        pop(drawerId);
                    }}
                    showCompanySelect={role.isSuperAdmin() || !role.isOneCompanyUser()}
                />
            ),
        });
    };

    const openUpdateAgentModal = () => {
        const drawerId = push({
            header: { title: translate('agents.editAgent') },
            content: (
                <UpdateAgentForm
                    agentId={selectedAgentId}
                    onSuccess={() => {
                        onAgentFormSubmitSuccess();
                        pop(drawerId);
                    }}
                />
            ),
        });
    };

    if (!portId) {
        return (
            <div>
                <ComboBox disabled label={label} markAsOptional={markAsOptional} />

                <Box marginTop={6}>
                    <Callout
                        variant="accent"
                        description={translate('selectAgent.mandatoryFields')}
                        Icon={IconInfoCircle}
                    />
                </Box>
            </div>
        );
    }

    if (agentListQuery.isInitialLoading || !agentListQuery.data) {
        return (
            <Box display="flex" flexDirection="column" gap={4}>
                <AgentSelectLabel label={label} markAsOptional={markAsOptional} />
                <Box height="32px">
                    <Skeleton height={32} />
                </Box>
            </Box>
        );
    }

    if (!agentListQuery.data.agents.length) {
        return (
            <Box display="flex" flexDirection="column" gap={4}>
                <AgentSelectLabel label={label} markAsOptional={markAsOptional} />
                <Box display="flex" alignItems="center" height="32px">
                    <Button type="button" LeadingIcon={IconPlus} onClick={() => openCreateAgentModal()}>
                        {translate('selectAgent.createAgent')}
                    </Button>
                </Box>
            </Box>
        );
    }

    const onChange = (option: AgentSelectOption | null) => {
        if (option?.value === 'createAgent') {
            openCreateAgentModal(option);
        } else {
            helpers.setValue(option?.value);
        }
    };

    return (
        <AsyncComboBox<AgentSelectOption>
            {...props}
            label={label}
            onChange={(_, meta) => onChange(meta.option)}
            optionsQueryKey={(search) => queries.agents.comboBoxOptions(getAgentListParams(search)).queryKey}
            optionsQueryFn={loadAgentOptions}
            selectedOptionQueryKey={(agentId) =>
                queries.agents.selectedComboBoxOption({ ...getAgentListParams(), ids: [agentId] }).queryKey
            }
            selectedOptionQueryFn={loadSelectedAgentOption}
            value={selectedAgentId || null}
            isClearable
            markAsOptional={markAsOptional}
            OptionComponent={AgentOption}
            ValueComponent={AgentValue}
            caption={
                selectedAgent && !selectedAgent.source ? (
                    <Button emphasis="low" type="button" onClick={openUpdateAgentModal} LeadingIcon={IconPencil}>
                        {translate('selectAgent.edit', {
                            additionalInfo: selectedAgent.name,
                        })}
                    </Button>
                ) : null
            }
        />
    );
};
